/**
 * SCOPE ON THE ELEMENT (2026-10-11, docs/bim-plan/32 §3 D; Matt: "select the element and describe
 * the scope ... demo the existing roofing, the existing eaves, gutters, etc. but keep the main
 * structure; we'll replace the insulation"). An existing element carries a SCOPE: the phase the
 * work happens in and, per PART, what happens to it:
 *
 *   scope: { phase: <PhaseId>, parts: { <partKey>: { verb } } }
 *
 *   keep      the part stays (existing)
 *   remove    the part is demolished in the scope phase, nothing replaces it
 *   replace   demolished and rebuilt in the scope phase (in kind; a "replace with" pick later)
 *   refinish  same part, new paint / stain / color: linework unchanged, notes and schedules only
 *
 * A part missing from `parts` is KEPT. Drawings read it through the per-piece phase channel
 * (bim-geometry/src/scope.ts tags each piece with the part it belongs to); notes, schedules and
 * the remodel percentage read `scopeRows`.
 *
 * Round 3: roofs. The part list is per category; a part may be SCOPE-ONLY (no piece of its own to
 * draw yet — insulation, underlayment, sheathing ride inside the roof body today): it still
 * counts for notes, schedules and specs.
 */
import type { ParamValue, PhaseRecord } from './types'

export type ScopeVerb = 'keep' | 'remove' | 'replace' | 'refinish'
export const SCOPE_VERBS: { value: ScopeVerb; label: string }[] = [
  { value: 'keep', label: 'Keep' },
  { value: 'remove', label: 'Remove' },
  { value: 'replace', label: 'Remove and replace' },
  { value: 'refinish', label: 'Refinish' },
]
export const SCOPE_KEY = 'scope'

export interface ElementScope {
  phase: string
  parts: Record<string, { verb: ScopeVerb }>
  /** NEW FINISH IN THE SCOPE PHASE (2026-10-14): a wall face troweled in a later phase gets its
   *  new finish here (material id per face) instead of overwriting the existing finish, so the
   *  existing phase still shows what was there. Built onto the peeled wall (wallScopeAfter.ts). */
  add?: Partial<Record<'exterior' | 'interior', string>> & {
    /** ROOF LAYERS (2026-10-30): new roofing troweled on an existing roof in its scope phase,
     *  per plane (the plane's bearing-edge node id → roofing material id) */
    roofing?: Record<string, string>
  }
  /** ROOF LAYERS BY PLANE (2026-11-02, Matt: "Demoing of roofing layers should be by roof plane,
   *  not by the whole modelled roof"): the roof's top layers (ROOF_PLANE_PARTS) per plane, the
   *  plane's bearing-edge node id → part → verb. A plane's verb is its own entry, else the
   *  whole-roof `parts` entry (the Scope panel), else keep; an explicit 'keep' here holds a plane
   *  back from a whole-roof mark. */
  planes?: Record<string, Record<string, { verb: ScopeVerb }>>
}

/** the roof parts that come off plane by plane (the top of the roof) */
export const ROOF_PLANE_PARTS = ['roofing', 'underlayment', 'sheathing']

export interface ScopePart {
  key: string
  label: string
  /** a load-bearing part: a Kept structural part never counts toward the remodel percentage */
  structural?: boolean
  /** no piece of its own to draw yet (it rides inside another) — scope still records it */
  scopeOnly?: boolean
}

export interface ScopePreset { id: string; label: string; parts: Record<string, ScopeVerb> }

/** ROOF PARTS — keys match bim-geometry/src/scope.ts#roofPieceKey */
export const ROOF_SCOPE_PARTS: ScopePart[] = [
  { key: 'roofing', label: 'Roofing' },
  { key: 'underlayment', label: 'Underlayment', scopeOnly: true },
  { key: 'sheathing', label: 'Sheathing', scopeOnly: true },
  { key: 'insulation', label: 'Insulation', scopeOnly: true },
  { key: 'structure', label: 'Rafters / structure', structural: true },
  { key: 'fascia', label: 'Fascia & frieze' },
  { key: 'gutters', label: 'Gutters & downspouts' },
  { key: 'soffit', label: 'Soffit' },
  { key: 'eaveFraming', label: 'Rafter tails, outlookers & barge' },
]

export const ROOF_SCOPE_PRESETS: ScopePreset[] = [
  { id: 'reroof', label: 'Re-roof', parts: { roofing: 'replace', underlayment: 'replace' } },
  {
    id: 'reroofEaves', label: 'Re-roof and new eaves',
    parts: { roofing: 'replace', underlayment: 'replace', fascia: 'replace', gutters: 'replace', soffit: 'replace', eaveFraming: 'replace' },
  },
  { id: 'newGutters', label: 'New gutters', parts: { gutters: 'replace' } },
]

export function scopePartsFor(category: string): ScopePart[] {
  if (category === 'roof') return ROOF_SCOPE_PARTS
  return []
}
export function scopePresetsFor(category: string): ScopePreset[] {
  if (category === 'roof') return ROOF_SCOPE_PRESETS
  return []
}

const VERBS = new Set<string>(SCOPE_VERBS.map((v) => v.value))
export function normalizeScope(raw: unknown): ElementScope | null {
  const r = (raw || {}) as Partial<ElementScope>
  if (typeof r.phase !== 'string' || !r.phase) return null
  const parts: ElementScope['parts'] = {}
  for (const [k, v] of Object.entries((r.parts || {}) as Record<string, unknown>)) {
    const verb = (v as { verb?: unknown } | null)?.verb
    if (typeof verb === 'string' && VERBS.has(verb) && verb !== 'keep') parts[k] = { verb: verb as ScopeVerb }
  }
  const add: NonNullable<ElementScope['add']> = {}
  for (const side of ['exterior', 'interior'] as const) {
    const v = (r.add as Record<string, unknown> | undefined)?.[side]
    if (typeof v === 'string' && v) add[side] = v
  }
  const rf = (r.add as Record<string, unknown> | undefined)?.roofing
  if (rf && typeof rf === 'object') {
    const planes = Object.fromEntries(Object.entries(rf as Record<string, unknown>).filter(([k, v]) => k && typeof v === 'string' && v)) as Record<string, string>
    if (Object.keys(planes).length) add.roofing = planes
  }
  const planes: NonNullable<ElementScope['planes']> = {}
  for (const [id, ps] of Object.entries(((r as { planes?: unknown }).planes || {}) as Record<string, unknown>)) {
    if (!id || !ps || typeof ps !== 'object') continue
    const one: Record<string, { verb: ScopeVerb }> = {}
    for (const [k, v] of Object.entries(ps as Record<string, unknown>)) {
      const verb = (v as { verb?: unknown } | null)?.verb
      if (ROOF_PLANE_PARTS.includes(k) && typeof verb === 'string' && VERBS.has(verb)) one[k] = { verb: verb as ScopeVerb }
    }
    if (Object.keys(one).length) planes[id] = one
  }
  return { phase: r.phase, parts, ...(Object.keys(add).length ? { add } : {}), ...(Object.keys(planes).length ? { planes } : {}) }
}

/** true when a scope says anything at all (a part off, replaced or refinished, or a new finish) */
export const scopeHasWork = (s: ElementScope | null): boolean =>
  !!s && (Object.keys(s.parts).length > 0 || !!(s.add && Object.keys(s.add).length)
    || Object.values(s.planes ?? {}).some((ps) => Object.values(ps).some((p) => p.verb !== 'keep')))

/** a roof plane's verb for one of its top layers (ROOF_PLANE_PARTS): the plane's own mark, else
 *  the whole roof's */
export function roofPlaneVerb(scope: ElementScope | null, nodeId: string | null | undefined, part: string): ScopeVerb {
  const own = nodeId ? scope?.planes?.[nodeId]?.[part]?.verb : undefined
  return own ?? scopeVerbOf(scope, part)
}

/** the roof's plane ids (its boundary nodes, one plane per bearing edge) */
export function roofPlaneIds(el: { params: Record<string, unknown> }): string[] {
  const nodes = (el.params.boundary as { nodes?: { id?: string }[] } | null)?.nodes ?? []
  return nodes.map((n) => n.id).filter((x): x is string => !!x)
}

/** does any plane of the roof take this verb on this part? */
export function roofAnyPlane(el: { params: Record<string, unknown> }, part: string, test: (v: ScopeVerb) => boolean): boolean {
  const scope = normalizeScope(el.params[SCOPE_KEY])
  if (!scope) return false
  const ids = roofPlaneIds(el)
  return ids.length ? ids.some((id) => test(roofPlaneVerb(scope, id, part))) : test(scopeVerbOf(scope, part))
}

export const scopeVerbOf = (scope: ElementScope | null, part: string): ScopeVerb =>
  scope?.parts[part]?.verb ?? 'keep'

/** the phase a new scope defaults to: the one after the element's own, else the last */
export function defaultScopePhase(phases: PhaseRecord[], phaseCreated: string | null | undefined): string | null {
  if (!phases.length) return null
  const sorted = [...phases].sort((a, b) => a.order - b.order)
  const i = phaseCreated ? sorted.findIndex((p) => p.id === phaseCreated) : 0
  return (sorted[Math.min(Math.max(i, 0) + 1, sorted.length - 1)].id as string)
}

/** the scope as plain rows (part, verb), kept parts left out — notes / schedules / specs read this */
export function scopeRows(category: string, raw: ParamValue | unknown): { part: ScopePart; verb: ScopeVerb }[] {
  const scope = normalizeScope(raw)
  if (!scope) return []
  // a roof's top layers marked plane by plane: the row reads the strongest verb any plane has
  const rank: Record<ScopeVerb, number> = { keep: 0, refinish: 1, replace: 2, remove: 3 }
  const verbOf = (key: string): ScopeVerb => {
    let v = scopeVerbOf(scope, key)
    if (category === 'roof' && ROOF_PLANE_PARTS.includes(key)) {
      for (const ps of Object.values(scope.planes ?? {})) {
        const pv = ps[key]?.verb
        if (pv && rank[pv] > rank[v]) v = pv
      }
    }
    return v
  }
  return scopePartsFor(category)
    .map((part) => ({ part, verb: verbOf(part.key) }))
    .filter((r) => r.verb !== 'keep')
}

// ---------- WALLS (round 4): a wall's parts are its assembly's real LAYERS ----------
const LAYER_FN_LABEL: Record<string, string> = {
  structure: 'structure', substrate: 'sheathing / substrate', insulation: 'insulation',
  finish: 'finish', membrane: 'weather barrier', air: 'air space',
}
interface LayerLike { function: string; materialId: string; thicknessFt?: number }
interface ScopeTarget {
  category: string
  assembly?: { layers: LayerLike[]; coreBoundary?: { start: number; end: number } } | null
}
type AssemblyLike = ScopeTarget['assembly']

/** LEGACY part key of assembly layer `i` (round 4 stored layers by POSITION). Still read, so a
 *  scope written before stable keys keeps working, but never written any more: a painted finish
 *  adds layers in front and shifts every position, so a stored position could land on the studs. */
export const layerPartKey = (i: number): string => `layer:${i}`

const sideOf = (i: number, cs: number, ce: number): 'ext' | 'core' | 'int' => (i < cs ? 'ext' : i > ce ? 'int' : 'core')

/**
 * STABLE LAYER KEYS (DEMOLISH LAYERS round, 2026-10-14): a layer is named by WHAT it is, its side
 * of the core and its material: `layer:ext:<materialId>`, `layer:core:<materialId>`,
 * `layer:int:<materialId>`, with `#2`, `#3` for a second layer of the same material on the same
 * side (counted from the face). Painting a finish on, clearing it, or a type with the same
 * materials leaves every other layer's key alone; a key whose layer is gone simply matches nothing.
 */
export function wallLayerKeys(assembly: AssemblyLike): string[] {
  const layers = assembly?.layers ?? []
  const cs = assembly?.coreBoundary?.start ?? 0
  const ce = assembly?.coreBoundary?.end ?? cs
  const seen = new Map<string, number>()
  const keys: string[] = new Array(layers.length)
  // exterior counts from the exterior face inward, interior from the interior face inward
  const idx = [...layers.keys()]
  const order = idx.filter((i) => i <= ce).concat(idx.filter((i) => i > ce).reverse())
  for (const i of order) {
    const base = `layer:${sideOf(i, cs, ce)}:${layers[i].materialId}`
    const n = (seen.get(base) ?? 0) + 1
    seen.set(base, n)
    keys[i] = n === 1 ? base : `${base}#${n}`
  }
  return keys
}

/** the verb on assembly layer `i`: its stable key, else a legacy positional key */
export function wallLayerVerb(scope: ElementScope | null, assembly: AssemblyLike, i: number): ScopeVerb {
  if (!scope) return 'keep'
  const k = wallLayerKeys(assembly)[i]
  return scope.parts[k]?.verb ?? scope.parts[layerPartKey(i)]?.verb ?? 'keep'
}

/** a scope with its legacy positional layer keys rewritten as stable keys (read against the
 *  wall's CURRENT assembly): the panel and the demolish commands read and write this */
export function migrateWallScope(scope: ElementScope | null, assembly: AssemblyLike): ElementScope | null {
  if (!scope) return null
  const keys = wallLayerKeys(assembly)
  const parts: ElementScope['parts'] = {}
  for (const [k, v] of Object.entries(scope.parts)) {
    const m = /^layer:(\d+)$/.exec(k)
    if (m) {
      const sk = keys[Number(m[1])]
      if (sk && !scope.parts[sk]) parts[sk] = v
      continue
    }
    parts[k] = v
  }
  return { phase: scope.phase, parts, ...(scope.add ? { add: { ...scope.add } } : {}) }
}

/** the parts of one element: a wall's layers (exterior first) plus its in-stud insulation; a
 *  roof's fixed list. `nameOf` turns a material id into its name. */
export function scopePartsForElement(el: ScopeTarget, nameOf: (materialId: string) => string): ScopePart[] {
  if (el.category === 'wall') {
    const layers = el.assembly?.layers ?? []
    if (!layers.length) return []
    const cs = el.assembly?.coreBoundary?.start ?? 0
    const ce = el.assembly?.coreBoundary?.end ?? cs
    const keys = wallLayerKeys(el.assembly)
    const parts: ScopePart[] = layers.map((l, i) => {
      const side = i < cs ? 'exterior' : i > ce ? 'interior' : 'core'
      return {
        key: keys[i],
        label: `${nameOf(l.materialId) || 'Layer'} (${side === 'core' ? 'core' : `${side} ${LAYER_FN_LABEL[l.function] ?? l.function}`})`,
        ...(side === 'core' ? { structural: true } : {}),
      }
    })
    parts.push({ key: 'insulation', label: 'Insulation in the studs', scopeOnly: true })
    return parts
  }
  return scopePartsFor(el.category)
}

/** the presets one element offers: a wall's are worked out from its own layers */
export function scopePresetsForElement(el: ScopeTarget): ScopePreset[] {
  if (el.category === 'wall') {
    const layers = el.assembly?.layers ?? []
    if (!layers.length) return []
    const cs = el.assembly?.coreBoundary?.start ?? 0
    const ce = el.assembly?.coreBoundary?.end ?? cs
    const keys = wallLayerKeys(el.assembly)
    const pick = (test: (i: number) => boolean): Record<string, ScopeVerb> =>
      Object.fromEntries(layers.map((_, i) => i).filter(test).map((i) => [keys[i], 'replace' as ScopeVerb]))
    return [
      { id: 'backToStuds', label: 'Back to studs', parts: { ...pick((i) => i < cs || i > ce), insulation: 'replace' } },
      { id: 'reside', label: 'Re-side', parts: pick((i) => i < cs) },
      { id: 'interiorFinish', label: 'New interior finish', parts: pick((i) => i > ce) },
    ]
  }
  return scopePresetsFor(el.category)
}

// ---------- DEMOLISH LAYERS (2026-10-14): the tool that marks layers by clicking them ----------
export type WallFace = 'exterior' | 'interior'

/** the layer indexes of one face, from the face inward to (not including) the core */
export function faceLayerOrder(assembly: AssemblyLike, face: WallFace): number[] {
  const n = assembly?.layers?.length ?? 0
  const cs = assembly?.coreBoundary?.start ?? 0
  const ce = assembly?.coreBoundary?.end ?? cs
  const out: number[] = []
  if (face === 'exterior') for (let i = 0; i < cs && i < n; i++) out.push(i)
  else for (let i = n - 1; i > ce; i--) out.push(i)
  return out
}

/** the layers of one face grouped the way they come off: a finish together with the finish
 *  layers stuck to it (brick and its mortar bed, tile and its setting bed), every other layer
 *  (sheathing, a weather barrier, rigid insulation) on its own. Face first. */
export function faceLayerGroups(assembly: AssemblyLike, face: WallFace): number[][] {
  const layers = assembly?.layers ?? []
  const groups: number[][] = []
  for (const i of faceLayerOrder(assembly, face)) {
    const g = groups[groups.length - 1]
    if (g && layers[i].function === 'finish' && layers[g[0]].function === 'finish') g.push(i)
    else groups.push([i])
  }
  return groups
}

export type DemoTarget =
  | { kind: 'layers'; face: WallFace; indexes: number[]; keys: string[]; materialId: string }
  | { kind: 'insulation'; face: WallFace }
  | { kind: 'studs'; face: WallFace }

const gone = (v: ScopeVerb) => v === 'remove' || v === 'replace'

/** what the next click on a wall face takes off: the outermost group of that face that is not
 *  already removed or replaced, then (an insulated wall) the insulation, then nothing but studs */
export function wallDemoTarget(el: ScopeTarget & { params: Record<string, unknown> }, face: WallFace): DemoTarget {
  const scope = migrateWallScope(normalizeScope(el.params[SCOPE_KEY]), el.assembly)
  const keys = wallLayerKeys(el.assembly)
  for (const g of faceLayerGroups(el.assembly, face)) {
    if (g.every((i) => gone(scopeVerbOf(scope, keys[i])))) continue
    return { kind: 'layers', face, indexes: g, keys: g.map((i) => keys[i]), materialId: el.assembly!.layers[g[0]].materialId }
  }
  if (el.params.insulation === true && scopeVerbOf(scope, 'insulation') === 'keep') return { kind: 'insulation', face }
  return { kind: 'studs', face }
}

/** one marked group on a wall (the markers the demolish tool shows): its keys, its verb, the
 *  material named on the marker, and the face it is on ('core' = the insulation in the studs) */
export interface DemoMark { face: WallFace | 'core'; keys: string[]; verb: ScopeVerb; materialId: string | null; insulation?: boolean }
export function wallDemoMarks(el: ScopeTarget & { params: Record<string, unknown> }): DemoMark[] {
  const scope = migrateWallScope(normalizeScope(el.params[SCOPE_KEY]), el.assembly)
  if (!scope) return []
  const keys = wallLayerKeys(el.assembly)
  const out: DemoMark[] = []
  for (const face of ['exterior', 'interior'] as WallFace[]) {
    for (const g of faceLayerGroups(el.assembly, face)) {
      const vs = g.map((i) => scopeVerbOf(scope, keys[i]))
      if (!vs.some(gone)) continue
      out.push({ face, keys: g.map((i) => keys[i]), verb: vs.every((x) => x === 'replace') ? 'replace' : 'remove', materialId: el.assembly!.layers[g[0]].materialId })
    }
  }
  const iv = scopeVerbOf(scope, 'insulation')
  if (gone(iv)) out.push({ face: 'core', keys: ['insulation'], verb: iv, materialId: null, insulation: true })
  return out
}

/** the layer indexes a wall's scope takes OUT of the finished wall (verb remove): the peeled
 *  wall in the scope phase is built without them. Replaced layers stay (rebuilt in kind). */
export function wallRemovedLayers(el: ScopeTarget & { params: Record<string, unknown> }): number[] {
  const scope = migrateWallScope(normalizeScope(el.params[SCOPE_KEY]), el.assembly)
  if (!scope) return []
  const keys = wallLayerKeys(el.assembly)
  const cs = el.assembly?.coreBoundary?.start ?? 0
  const ce = el.assembly?.coreBoundary?.end ?? cs
  const out: number[] = []
  for (let i = 0; i < keys.length; i++) {
    if (i >= cs && i <= ce) continue   // the studs never peel: the whole wall is demolished instead
    if (scopeVerbOf(scope, keys[i]) === 'remove') out.push(i)
  }
  return out
}

/** DEMOLITION TABULATION (bim-plan/28 round 11): does this element's scope take out its
 *  STRUCTURE — a wall's core layer, a roof's rafters — removed or replaced? A wall stripped
 *  "back to studs" or re-sided keeps its structure, and so does a re-roof: not demolition. */
export function scopeRemovesStructure(el: ScopeTarget & { params: Record<string, unknown> }): boolean {
  const scope = normalizeScope(el.params[SCOPE_KEY])
  if (!scope) return false
  if (el.category === 'wall') {
    const cs = el.assembly?.coreBoundary?.start ?? 0
    const ce = el.assembly?.coreBoundary?.end ?? cs
    for (let i = cs; i <= ce; i++) {
      const v = wallLayerVerb(scope, el.assembly, i)
      if (v === 'remove' || v === 'replace') return true
    }
    return false
  }
  return scopePartsForElement(el, () => '').some((p) => {
    if (!p.structural) return false
    const v = scopeVerbOf(scope, p.key)
    return v === 'remove' || v === 'replace'
  })
}

// ---------- ROOF LAYERS (2026-10-30, Matt: "I think we need it to act like walls"): the demolish
// tool and the trowel on an existing roof's TOP. A roof comes off from the top down, the way a
// wall face comes off from its face in: the roofing with its underlayment, then the sheathing.
// The rafters are refused (that is demolishing the roof). The marks are the roof's scope parts
// (ROOF_SCOPE_PARTS keys), so the Scope panel, the drawings and the notes read the one record. ----------

/** the groups a roof's top comes off in, top first; sheathing only when the assembly has layers
 *  above its core */
export function roofLayerGroups(assembly: AssemblyLike): string[][] {
  const groups = [['roofing', 'underlayment']]
  if ((assembly?.coreBoundary?.start ?? 0) > 0) groups.push(['sheathing'])
  return groups
}

export type RoofDemoTarget = { kind: 'layers'; keys: string[] } | { kind: 'structure' }

type RoofLike = ScopeTarget & { params: Record<string, unknown> }

/** what the next click on a roof PLANE takes off: its topmost group not already removed or
 *  replaced, else nothing but the rafters. `nodeId` = the plane (its bearing-edge node); null =
 *  read the whole roof's marks only. */
export function roofDemoTarget(el: RoofLike, nodeId?: string | null): RoofDemoTarget {
  const scope = normalizeScope(el.params[SCOPE_KEY])
  for (const g of roofLayerGroups(el.assembly)) {
    if (g.every((k) => gone(roofPlaneVerb(scope, nodeId, k)))) continue
    return { kind: 'layers', keys: g }
  }
  return { kind: 'structure' }
}

/** one marked group on one roof plane (the demolish tool's markers) */
export interface RoofDemoMark { nodeId: string | null; keys: string[]; verb: ScopeVerb }
export function roofDemoMarks(el: RoofLike): RoofDemoMark[] {
  const scope = normalizeScope(el.params[SCOPE_KEY])
  if (!scope) return []
  const ids = roofPlaneIds(el)
  const out: RoofDemoMark[] = []
  for (const id of ids.length ? ids : [null]) {
    for (const g of roofLayerGroups(el.assembly)) {
      const vs = g.map((k) => roofPlaneVerb(scope, id, k))
      if (!vs.some(gone)) continue
      out.push({ nodeId: id, keys: g, verb: vs.every((x) => x === 'replace') ? 'replace' : 'remove' })
    }
  }
  return out
}

/** a roof plane's sheathing in its scope phase: 'demo' only (off, nothing back), 'replace' (off
 *  and new: put back the same, or new roofing troweled onto that plane), or null. `nodeId` null =
 *  the roof as a whole (any plane). */
export function roofSheathingWork(el: RoofLike, nodeId?: string | null): 'demo' | 'replace' | null {
  const scope = normalizeScope(el.params[SCOPE_KEY])
  if (!scope) return null
  const one = (id: string | null): 'demo' | 'replace' | null => {
    const v = roofPlaneVerb(scope, id, 'sheathing')
    if (v === 'replace') return 'replace'
    if (v !== 'remove') return null
    const add = scope.add?.roofing ?? {}
    return (id ? !!add[id] : Object.keys(add).length > 0) ? 'replace' : 'demo'
  }
  if (nodeId !== undefined) return one(nodeId)
  const ids = roofPlaneIds(el)
  const ws = (ids.length ? ids : [null]).map(one)
  return ws.includes('replace') ? 'replace' : ws.includes('demo') ? 'demo' : null
}
